import type {
  ShortVideo,
  ShortVideoChannel,
  ShortVideoTranslation,
} from '@services/firebase/short-videos'
import {
  getLearningLocaleFallbacks,
  normalizeLearningLocale,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import { getYoutubeThumbnailUrl } from '@shared/media/youtube/youtube-embed-url'

export function normalizeShortVideoLocale(locale: string) {
  return normalizeLearningLocale(locale)
}

export function getShortVideoTranslationText(
  translations: ShortVideoTranslation[] | undefined,
  locale: string,
) {
  if (!translations || translations.length === 0) {
    return null
  }

  if (normalizeShortVideoLocale(locale) === 'es') {
    return resolveTranslatedText(translations, locale) ?? null
  }

  const fallbacks = getLearningLocaleFallbacks(locale)

  for (const code of fallbacks) {
    const translation = translations.find((item) => item.code === code)

    if (translation?.text) {
      return translation.text
    }
  }

  return translations.find((translation) => translation.text)?.text ?? null
}

export function getShortVideoThumbnail(video: ShortVideo) {
  if (video.featuredImage) {
    return video.featuredImage
  }

  if (video.youtubeId) {
    return getYoutubeThumbnailUrl(video.youtubeId)
  }

  return null
}

export function getUniqueShortVideosById(videos: ReadonlyArray<ShortVideo>) {
  const seenVideoIds = new Set<string>()

  return videos.filter((video) => {
    if (seenVideoIds.has(video.id)) {
      return false
    }

    seenVideoIds.add(video.id)
    return true
  })
}

export function getShortVideoDurationSeconds(video: ShortVideo) {
  const firstSegment = video.content[0]
  const lastSegment = video.content[video.content.length - 1]

  if (
    typeof firstSegment?.start !== 'number' ||
    typeof lastSegment?.end !== 'number'
  ) {
    return null
  }

  return Math.max(0, lastSegment.end - firstSegment.start)
}

export function formatShortVideoDuration(seconds: number | null) {
  if (seconds === null) {
    return '0:00'
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export function normalizePracticeAnswer(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isShortVideoPracticeMatch({
  actual,
  expected,
}: {
  readonly actual: string
  readonly expected: string
}) {
  const normalizedActual = normalizePracticeAnswer(actual)
  const normalizedExpected = normalizePracticeAnswer(expected)

  if (!normalizedActual || !normalizedExpected) {
    return false
  }

  return normalizedExpected.includes(normalizedActual)
}

export function getShortVideoChannelTitle({
  channelId,
  channels,
}: {
  readonly channelId?: string
  readonly channels: ShortVideoChannel[]
}) {
  if (!channelId) {
    return null
  }

  return channels.find((channel) => channel.id === channelId)?.title ?? null
}
